import { PageWithTitle, PageWithTitleError, PageWithTitleLoading } from 'src/shared/components/PageWithTitle';
import { WithForm } from 'src/shared/HOC/WithForm';
import { CompanySelectorBar } from './components/CompanySelectorBar/CompanySelectorBar';
import {
  useGetCompaniesQuery,
  useGetSelfFundedAccountQuery,
  useGetSelfFundedAccountsQuery
} from 'src/shared/api/company';
import { useGetUserHoldingQuery } from 'src/shared/api/holding';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useGetColumns } from './constants';
import { Table } from 'src/shared/components/Table/Table';
import { useFormState } from 'react-final-form';
import { ShowError } from 'src/shared/components/ShowError';
import { Divider, Typography } from '@mui/material';
import { ActionsBar } from 'src/modules/SaldoView/components/ActionsBar';
import { useTranslation } from 'react-i18next';
import { mapHoldingCompaniesToSelect } from 'src/modules/SaldoView/utils';
import { type CompanyToSelect } from 'src/modules/SaldoView/types';
import { getLocaleFromIsoCode } from 'src/shared/utils/locale';

const SaldoViewContent = ({ dataToSelect }: { dataToSelect: CompanyToSelect[] }) => {
  const { values } = useFormState();
  const { data, isLoading, isError, error } = useGetSelfFundedAccountQuery(values.selectedCompany);
  const { data: userHolding } = useGetUserHoldingQuery();
  const { t } = useTranslation('overview');
  const pageTitle = t('self funding.saldo');
  const hasDirectDebitIban =
    userHolding?.companies.some(company => company?.metadata?.billing?.selfFundingDirectDebitIban) ?? false;

  const columns = useGetColumns({
    locale: getLocaleFromIsoCode(userHolding?.territory?.isoCode),
    currency: userHolding?.territory?.currency
  });
  if (isLoading) {
    return <PageWithTitleLoading title={pageTitle} />;
  }
  if (isError || !data) {
    return (
      <PageWithTitle title={pageTitle} contentWrapperProps={{ sx: { padding: '48px 64px' } }}>
        <CompanySelectorBar account={data?.account} dataToSelect={dataToSelect} />
        <ShowError additionalText={t('self funding.error')} error={error} />
      </PageWithTitle>
    );
  }
  return (
    <PageWithTitle
      titleContent={<ActionsBar account={data.account} hasDirectDebitIban={hasDirectDebitIban} />}
      title={pageTitle}
      contentWrapperProps={{ sx: { padding: '48px 64px' } }}
    >
      <CompanySelectorBar account={data.account} dataToSelect={dataToSelect} />
      <Grid2 xs={12} mt={10}>
        <Typography color="#8998AF" variant="h3">
          {t('self funding.refills')}
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Table rows={data.topUps} columns={columns} />
      </Grid2>
    </PageWithTitle>
  );
};

export const SaldoView = () => {
  const { t } = useTranslation('overview');
  const pageTitle = t('self funding.saldo');
  const { data, isLoading, isError, error } = useGetCompaniesQuery();
  const {
    data: accounts,
    isLoading: isAccountsLoading,
    isError: isAccountError,
    error: accountError
  } = useGetSelfFundedAccountsQuery();
  const dataToSelect = mapHoldingCompaniesToSelect(data?.data ?? [], accounts ?? []);
  if (isLoading || isAccountsLoading) {
    return <PageWithTitleLoading title={pageTitle} />;
  }
  if (isError || !data || isAccountError || !accounts) {
    return <PageWithTitleError error={error ?? accountError} title={pageTitle} />;
  }
  return (
    <WithForm initialValues={{ selectedCompany: dataToSelect[0]?.value }}>
      <SaldoViewContent dataToSelect={dataToSelect} />
    </WithForm>
  );
};
